import React, { useEffect, useState } from 'react';
import Topbar from '../components/Topbar';
import { supabase } from '../lib/supabase';
import { Send, Megaphone, Bell } from 'lucide-react';

export default function Notifications() {
  const [form, setForm] = useState({ title: '', body: '', type: 'promo_deal' });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [pushTokenCount, setPushTokenCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchNotificationData(); }, []);

  async function fetchNotificationData() {
    // `email` is no longer joinable — migration 0010 revokes column-level
    // SELECT on it, and PostgREST fails the whole query rather than dropping
    // the field, so asking for it would empty this list entirely.
    const [{ data, error }, { count }] = await Promise.all([
      supabase.from('notifications').select('*, profiles(full_name)').order('created_at', { ascending: false }).limit(50),
      supabase.from('push_tokens').select('id', { count: 'exact', head: true }),
    ]);
    if (error) setResult({ success: false, message: `Couldn't load history: ${error.message}` });
    setNotifications(data ?? []);
    setPushTokenCount(count ?? 0);
    setLoading(false);
  }

  async function sendPromo() {
    if (!form.title || !form.body) return;
    setSending(true);
    setResult(null);
    try {
      const { data, error } = await supabase.functions.invoke('send-promo', {
        body: { title: form.title, body: form.body, data: { type: form.type } },
      });
      if (error) throw error;
      setResult({ success: true, message: `✅ Sent to ${data.sent} users successfully!` });
      setForm({ title: '', body: '', type: 'promo_deal' });
      fetchNotificationData();
    } catch (err: any) {
      setResult({ success: false, message: `❌ Failed: ${err.message}` });
    } finally {
      setSending(false);
    }
  }

  const templates = [
    { label: '🔥 Flash Sale',       title: 'Flash Sale 🔥',        body: '20% off all hair oils today only! Shop now in the Zorax store.' },
    { label: '🆕 New Arrivals',     title: 'New Products In! 🛍️',  body: 'Check out our latest arrivals — new hair care products just dropped in the store!' },
    { label: '🎁 Weekend Special',  title: 'Weekend Special 🎁',    body: 'This weekend only — free delivery on all orders over £30. Don\'t miss out!' },
    { label: '⭐ Loyalty Reward',   title: 'You\'ve Earned It! ⭐', body: 'Double points on all purchases this week. Stock up and earn more rewards!' },
  ];

  return (
    <div>
      <Topbar title="Notifications" />
      <div style={{ padding: '24px 28px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 20, marginBottom: 20 }}>

          {/* Compose */}
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
              <div style={{ width: 38, height: 38, borderRadius: 10, background: 'var(--primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Megaphone size={18} color="var(--primary)" />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>Send Promo Broadcast</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Sends push notification to all users</div>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>Type</label>
                <select className="input" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                  <option value="promo_deal">Promo Deal</option>
                  <option value="hair_tip">Hair Tip</option>
                  <option value="points_earned">Points Reward</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>Title</label>
                <input className="input" placeholder="e.g. Flash Sale 🔥" value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>Message</label>
                <textarea className="input" rows={4} placeholder="Write your notification message..."
                  value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                  style={{ resize: 'vertical' }} />
              </div>
            </div>

            {result && (
              <div style={{
                marginTop: 14, padding: '10px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500,
                background: result.success ? 'var(--success-light)' : 'var(--danger-light)',
                color: result.success ? 'var(--success)' : 'var(--danger)',
              }}>
                {result.message}
              </div>
            )}

            <button className="btn btn-primary" style={{ marginTop: 18, width: '100%', justifyContent: 'center', gap: 8 }}
              onClick={sendPromo} disabled={sending || !form.title || !form.body}>
              <Send size={15} />
              {sending ? 'Sending...' : 'Send to All Users'}
            </button>
          </div>

          {/* Templates */}
          <div>
            <div className="card">
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>Broadcast Reach</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 14, borderRadius: 14, background: 'var(--primary-light)', color: 'var(--primary)', marginBottom: 16 }}>
                <Bell size={18} />
                <div>
                  <div style={{ fontSize: 22, fontWeight: 850, lineHeight: 1 }}>{pushTokenCount}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, marginTop: 3 }}>registered push tokens</div>
                </div>
              </div>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>Quick Templates</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {templates.map(t => (
                  <button key={t.label} onClick={() => setForm(f => ({ ...f, title: t.title, body: t.body }))}
                    style={{
                      padding: '12px 14px', borderRadius: 10,
                      border: '1px solid var(--border)', background: '#fff',
                      cursor: 'pointer', textAlign: 'left',
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--primary)')}
                    onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--border)')}
                  >
                    <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', marginBottom: 3 }}>{t.label}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.4 }}>{t.title}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="page-header" style={{ marginBottom: 16 }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16 }}>Recent Notifications</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Latest messages stored in Supabase</div>
            </div>
            <span className="badge badge-primary">{notifications.length} shown</span>
          </div>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Type</th>
                  <th>Title</th>
                  <th>Message</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {notifications.map(notification => (
                  <tr key={notification.id}>
                    <td>
                      <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{notification.profiles?.full_name ?? 'Unknown'}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'ui-monospace, Menlo, monospace' }}>{notification.user_id}</div>
                    </td>
                    <td><span className="badge badge-primary">{notification.type}</span></td>
                    <td style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{notification.title}</td>
                    <td style={{ maxWidth: 420 }}>{notification.body}</td>
                    <td>
                      <span className={notification.is_read ? 'badge badge-success' : 'badge badge-warning'}>
                        {notification.is_read ? 'Read' : 'Unread'}
                      </span>
                    </td>
                    <td>{notification.created_at ? new Date(notification.created_at).toLocaleString('en-GB') : '—'}</td>
                  </tr>
                ))}
                {notifications.length === 0 && !loading && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40 }}>No notifications yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
